import { useEffect, useRef, useState } from "react";

/**
 * Хук, который возвращает отложенное значение, обновляющееся после указанной задержки
 * @param value Значение для отложенного обновления
 * @param delay Задержка в миллисекундах
 * @returns Отложенное значение
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Хук, который возвращает отложенный callback, обновляющийся после указанной задержки
 * @param callback Callback для отложенного вызова
 * @param delay Задержка в миллисекундах
 * @returns Отложенный callback
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function debouncedCallback(...args: Parameters<T>) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }

  return debouncedCallback as T;
}
